import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, IndianRupee, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { KpiTile } from '@/components/charts/KpiTile';
import { TrendChart } from '@/components/charts/TrendChart';
import { DeltaBadge } from '@/components/charts/DeltaBadge';
import { EmptyState } from '@/components/charts/EmptyState';
import { MotionCard } from '@/components/motion/MotionCard';
import { useDashboard } from '@/hooks/useDashboardContext';
import { useItemDetail } from '@/hooks/useItemDetail';
import { useOutlets } from '@/hooks/useOutlets';
import { fmtISTRange, formatINRCompact, formatNumberCompact } from '@/lib/format';

export function ItemDetailPage() {
  const { itemId = '' } = useParams<{ itemId: string }>();
  const { period, outletId } = useDashboard();
  const { data: outlets = [] } = useOutlets();
  const { data, isLoading, isError } = useItemDetail({ itemId, outletId, period });

  const outletName = (id: string) => outlets.find((o) => o.id === id)?.name ?? id;

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/sales"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to sales
        </Link>
        <SectionHeader
          title={data?.itemName ?? (isLoading ? 'Loading…' : 'Item')}
          description={
            data
              ? `${data.categoryName ?? 'Uncategorized'} · ${data.current.label} · ${fmtISTRange(data.current.start, data.current.end)} vs ${fmtISTRange(data.previous.start, data.previous.end)}`
              : ''
          }
        />
      </div>

      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}
      {isError && (
        <EmptyState title="Could not load item" description="Check the network tab for details." />
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <KpiTile
              label="Units sold"
              icon={<Package className="h-3.5 w-3.5" />}
              metric={data.unitsMetric}
              format={formatNumberCompact}
              sparkline={data.trend.map((p) => p.current)}
              comparisonRange={{ start: data.previous.start, end: data.previous.end }}
            />
            <KpiTile
              label="Gross sales"
              icon={<IndianRupee className="h-3.5 w-3.5" />}
              metric={data.salesMetric}
              format={formatINRCompact}
              sparkline={data.trend.map((p) => p.current)}
              comparisonRange={{ start: data.previous.start, end: data.previous.end }}
              delay={0.05}
            />
          </div>

          <MotionCard>
            {data.trend.length > 0 ? (
              <TrendChart
                title="Units sold per day"
                data={data.trend}
                formatValue={formatNumberCompact}
              />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Units sold per day</CardTitle>
                </CardHeader>
                <CardContent>
                  <EmptyState title="No daily data" description="This item didn't sell in the selected period." />
                </CardContent>
              </Card>
            )}
          </MotionCard>

          <MotionCard delay={0.05}>
            <Card>
              <CardHeader>
                <CardTitle>Outlets selling this item</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.outletBreakdown.length === 0 ? (
                  <div className="p-6">
                    <EmptyState title="No outlets" description="Nothing to break down." />
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="border-b border-border/60 bg-muted/30 text-2xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Outlet</th>
                        <th className="px-3 py-2 text-right font-medium">Units</th>
                        <th className="px-3 py-2 text-right font-medium">Sales</th>
                        <th className="px-3 py-2 text-right font-medium">vs prev</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {data.outletBreakdown.map((row) => {
                        const share = data.totalUnits > 0 ? (row.units / data.totalUnits) * 100 : 0;
                        return (
                          <tr key={row.outletId} className="hover:bg-muted/30">
                            <td className="px-3 py-2">
                              <Link
                                to={`/outlets/${row.outletId}`}
                                className="font-medium hover:underline"
                              >
                                {outletName(row.outletId)}
                              </Link>
                              <Badge variant="muted" className="ml-2 text-2xs">
                                {share.toFixed(0)}% share
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums">
                              {formatNumberCompact(row.units)}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums text-muted-foreground">
                              {formatINRCompact(row.sales)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <DeltaBadge
                                deltaPct={row.metric.deltaPct}
                                comparable={row.metric.comparable}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </MotionCard>
        </>
      )}
    </div>
  );
}
